import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ComercialLayout } from '../../components/comercial/ComercialLayout';
import { LoadingState } from '../../components/comercial/shared';
import { useAuth } from '../../context/AuthContext';
import { Target, Trophy, TrendingUp, Beaker, FileText, Settings } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function ObjetivoBar({ label, actual, objetivo, color }) {
  const pct = objetivo > 0 ? Math.min(Math.round((actual / objetivo) * 100), 100) : 0;
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-zinc-400 font-['Manrope']">{label}</span>
        <span className="text-sm text-white">
          <span className="text-xl font-light font-['Outfit']">{actual}</span>
          <span className="text-zinc-500"> / {objetivo}</span>
        </span>
      </div>
      <div className="w-full h-2.5 bg-white/5 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-700 ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <p className="text-xs text-zinc-500 mt-1.5">{pct}% completado</p>
    </div>
  );
}

export default function ObjetivosPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/comercial/mis-resultados`, { withCredentials: true });
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching objetivos:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <ComercialLayout
      title="Objetivos"
      subtitle="Tu progreso comercial"
      onLogout={handleLogout}
    >
      {loading ? (
        <LoadingState />
      ) : (
        <div className="space-y-6" data-testid="objetivos-page">
          {/* Objetivos mensual y anual */}
          <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
            <h3 className="text-lg font-light text-white font-['Outfit'] mb-6 flex items-center gap-2">
              <Target className="w-5 h-5 text-sky-400" />
              Objetivos de contratos
            </h3>
            <div className="space-y-6">
              <ObjetivoBar
                label="Objetivo mensual"
                actual={stats?.contratos_mes || 0}
                objetivo={stats?.objetivo_mes || 0}
                color="bg-gradient-to-r from-sky-500 to-sky-400"
              />
              <ObjetivoBar
                label="Objetivo anual"
                actual={stats?.contratos_ano || 0}
                objetivo={stats?.objetivo_ano || 0}
                color="bg-gradient-to-r from-emerald-500 to-emerald-400"
              />
            </div>
          </div>

          {/* KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-5 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center">
                  <Beaker className="w-5 h-5 text-purple-400" />
                </div>
                <span className="text-sm text-zinc-400">Pruebas realizadas</span>
              </div>
              <p className="text-3xl font-light text-white font-['Outfit']" data-testid="objetivos-pruebas-realizadas">
                {stats?.pruebas_realizadas || 0}
              </p>
            </div>
            <div className="p-5 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-emerald-400" />
                </div>
                <span className="text-sm text-zinc-400">Contratos cerrados (año)</span>
              </div>
              <p className="text-3xl font-light text-white font-['Outfit']" data-testid="objetivos-contratos">
                {stats?.contratos_ano || 0}
              </p>
            </div>
            <div className="p-5 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-amber-400" />
                </div>
                <span className="text-sm text-zinc-400">Conversión</span>
              </div>
              <p className="text-3xl font-light text-white font-['Outfit']" data-testid="objetivos-conversion">
                {stats?.conversion || 0}%
              </p>
            </div>
          </div>

          {/* Ranking */}
          {stats?.ranking?.length > 0 && (
            <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
              <h3 className="text-lg font-light text-white font-['Outfit'] mb-4 flex items-center gap-2">
                <Trophy className="w-5 h-5 text-amber-400" />
                Ranking del equipo
              </h3>
              <div className="space-y-2">
                {stats.ranking.map((item, index) => (
                  <div
                    key={index}
                    className={`flex items-center gap-4 p-3 rounded-xl ${item.nombre === user?.name ? 'bg-sky-500/10 border border-sky-500/20' : ''}`}
                  >
                    <span className={`w-7 text-center text-sm font-medium ${index === 0 ? 'text-amber-400' : 'text-zinc-500'}`}>
                      #{index + 1}
                    </span>
                    <p className={`flex-1 text-sm ${item.nombre === user?.name ? 'text-sky-300' : 'text-white'}`}>{item.nombre}</p>
                    <span className="text-sm text-zinc-400">{item.contratos} contratos</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Nota configuración */}
          <div className="flex items-center gap-3 p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
            <Settings className="w-4 h-4 text-zinc-500 flex-shrink-0" />
            <p className="text-xs text-zinc-500 font-['Manrope']">
              Los objetivos se configurarán desde el Portal de Administración (próxima fase) y se sincronizarán con Odoo.
            </p>
          </div>
        </div>
      )}
    </ComercialLayout>
  );
}
